/** Default when campaign has no winnerCount / requiredWinnerCount (backend create default). */
export const REQUIRED_WINNER_COUNT = 1;

/**
 * Prefer winnerInfos; fall back to singular winnerInfo for legacy campaigns.
 * @returns {Array<{ _id: string, username: string|null, image: string|null }>}
 */
export function getWinnerInfos(campaignOrState) {
  if (!campaignOrState || typeof campaignOrState !== 'object') return [];

  const infos = campaignOrState.winnerInfos;
  if (Array.isArray(infos) && infos.length > 0) {
    return infos.filter(Boolean);
  }

  // Nested campaign on public preview payload
  const nested = campaignOrState.campaign;
  if (nested && nested !== campaignOrState) {
    const nestedInfos = getWinnerInfos(nested);
    if (nestedInfos.length) return nestedInfos;
  }

  if (campaignOrState.winnerInfo) {
    return [campaignOrState.winnerInfo];
  }
  if (nested && nested.winnerInfo) {
    return [nested.winnerInfo];
  }
  return [];
}

/** Winner id slots from winners[] (or legacy winnerUserId). */
export function getWinners(campaign) {
  if (!campaign || typeof campaign !== 'object') return [];
  if (Array.isArray(campaign.winners) && campaign.winners.length > 0) {
    return campaign.winners.filter(Boolean);
  }
  if (campaign.winnerUserId) {
    return [{ userId: campaign.winnerUserId, entryId: campaign.winnerEntryId || null }];
  }
  return [];
}

/**
 * How many winners this campaign requires (admin-configurable via winnerCount).
 * Prefer winnerCount, then requiredWinnerCount, then default 1.
 */
export function getRequiredWinnerCount(campaign) {
  if (!campaign || typeof campaign !== 'object') return REQUIRED_WINNER_COUNT;

  const fromWinnerCount = Number(campaign.winnerCount);
  if (Number.isFinite(fromWinnerCount) && fromWinnerCount > 0) return fromWinnerCount;

  const fromRequired = Number(campaign.requiredWinnerCount);
  if (Number.isFinite(fromRequired) && fromRequired > 0) return fromRequired;

  // Nested campaign (e.g. public preview / winners history)
  const nested = campaign.campaign;
  if (nested && nested !== campaign) {
    return getRequiredWinnerCount(nested);
  }

  return REQUIRED_WINNER_COUNT;
}

export function getSelectedWinnerCount(campaign) {
  const winners = getWinners(campaign);
  if (winners.length) return winners.length;
  return getWinnerInfos(campaign).length;
}

export function isSelectionComplete(campaign) {
  if (!campaign) return false;
  if (campaign.selectionComplete === true) return true;
  // Legacy: winner_selected with only singular winner fields
  if (campaign.status === 'winner_selected' && getSelectedWinnerCount(campaign) > 0) {
    return true;
  }
  return getSelectedWinnerCount(campaign) >= getRequiredWinnerCount(campaign);
}

export function getRemainingWinnerSlots(campaign) {
  if (isSelectionComplete(campaign)) return 0;
  const remaining = getRequiredWinnerCount(campaign) - getSelectedWinnerCount(campaign);
  return remaining > 0 ? remaining : 0;
}

export function getSelectedWinnerUserIds(campaign) {
  return getWinners(campaign)
    .map((w) => (w && w.userId != null ? String(w.userId) : ''))
    .filter(Boolean);
}

/** Display string: "alice, bob" or "No winners selected". */
export function formatWinnersLabel(campaignOrState, emptyLabel = 'No winners selected') {
  const infos = getWinnerInfos(campaignOrState);
  if (!infos.length) return emptyLabel;
  return infos
    .map((w) => (w && (w.username || w._id)) || 'Unknown')
    .join(', ');
}

/** Progress like "1 / 3" using campaign winnerCount. */
export function formatWinnerProgress(campaign) {
  const selected = getSelectedWinnerCount(campaign);
  const required = getRequiredWinnerCount(campaign);
  return `${selected} / ${required}`;
}
